import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import aboutHero from "@/assets/about-hero.jpg";
import { blogArticles } from "@/data/blogArticles";
import { ArrowLeft, ExternalLink } from "lucide-react";
import LinkedText from "@/components/LinkedText";

const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <SEO title="Article Not Found | Vaughan McLean" description="This article is no longer available." noIndex />
        <PageHero backgroundImage={aboutHero} title="Article Not Found" subtitle="Blog" />
        <section className="section-dark py-24">
          <div className="container mx-auto px-8 lg:px-16 text-center">
            <p className="text-muted-foreground font-body mb-8">
              The article you are looking for could not be found.
            </p>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-primary font-body text-sm tracking-[0.15em] uppercase hover:opacity-80 transition-opacity"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={article.seoTitle || `${article.title} | Vaughan McLean`}
        description={article.excerpt || `Read "${article.title}" by ${article.author} on the Vaughan McLean blog.`}
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          datePublished: article.date,
          author: { "@type": "Person", name: article.author },
          publisher: {
            "@type": "Organization",
            name: "Vaughan McLean",
            logo: { "@type": "ImageObject", url: "https://www.vaughanmclean.com/logo-15b.png" },
          },
          mainEntityOfPage: `https://www.vaughanmclean.com/news/${article.slug}`,
        }}
      />
      <PageHero backgroundImage={aboutHero} title="Blog" subtitle="News & Insights" />

      <section className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          <div className="max-w-3xl">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-primary font-body text-sm tracking-[0.15em] uppercase hover:opacity-80 transition-opacity mb-12"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs font-body text-muted-foreground mb-1">{article.date}</p>
              <span className="inline-block text-[10px] tracking-[0.2em] uppercase font-body text-primary mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-3xl lg:text-4xl text-foreground mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-muted-foreground font-body mb-12 italic">
                {article.author}
              </p>

              {article.images && article.images.length > 0 && (
                <div className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {article.images.map((image) => (
                    <figure key={image.src} className="overflow-hidden border border-primary/30 bg-secondary">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="aspect-[4/3] h-full w-full object-cover object-top"
                      />
                    </figure>
                  ))}
                </div>
              )}

              <div className="space-y-6">
                {article.content.map((paragraph, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.05 }}
                    className="text-muted-foreground font-body leading-relaxed text-base"
                  >
                    <LinkedText text={paragraph} />
                  </motion.p>
                ))}
              </div>

              {article.externalLink && (
                <a
                  href={article.externalLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-10 inline-flex items-center gap-2 border-b border-primary pb-1 font-body text-xs uppercase tracking-[0.2em] text-primary transition-colors hover:border-foreground hover:text-foreground"
                >
                  {article.externalLink.label}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}

              {article.documentLink && (
                <a
                  href={article.documentLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-10 inline-flex items-center gap-2 border-b border-primary pb-1 font-body text-xs uppercase tracking-[0.2em] text-primary transition-colors hover:border-foreground hover:text-foreground"
                >
                  {article.documentLink.label}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
            </motion.div>

            <div className="border-t border-border mt-16 pt-8">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-primary font-body text-sm tracking-[0.15em] uppercase hover:opacity-80 transition-opacity"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to All Articles
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ArticlePage;
